import { Button, Card, Drawer, Layout, Menu, Result, theme } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppBreakpoint } from '@/contexts/BreakpointContext';
import { useSyncedPageTabs } from '@/hooks/useSyncedPageTabs';
import { rememberWorkspaceProjectTabTitle } from '@/hooks/workspaceTabProjectTitleCache';
import { UserWatermark } from '@/hooks/useWatermark';
import { getProjestsApi } from '@services/Selection';
import AppHeader from './AppHeader';
import KeepAliveOutlet from './KeepAliveOutlet';
import PageTabs from './PageTabs';
import SearchModal from './SearchModal';
import SelectionSider from './SelectionSider';
import { buildSelectionWorkspaceMenuEntries } from './data/selectionMenuItems';
import { getSelectionMenuSelectedKeys } from './selectionSiderSelection';
import { SELECTION_WORKSPACE_PARENT_PATH } from './selectionWorkspaceConfig';
import { isWorkspaceFixedTableScrollPath } from './workspaceFixedTableScrollPaths';
import { useEnableTableScrollYLayout } from './tableScrollLayout';

const { Content } = Layout;

const LS_SELECTION_WORKSPACE_COLLAPSED = 'selection_workspace_sider_collapsed';

const branchNoShrinkStyle = { flexShrink: 0 };

const rightColumnStyle = {
    minWidth: 0,
    minHeight: 0,
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
};

function safeLsSet(key, value) {
    try {
        localStorage.setItem(key, value);
    } catch {
        // quota exceeded or private-browsing mode
    }
}

export default function SelectionWorkspaceLayout() {
    const { pid } = useParams();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { token } = theme.useToken();
    const { isMobile } = useAppBreakpoint();
    const enableTableScrollYLayout = useEnableTableScrollYLayout();
    const useFixedTableScrollChain = enableTableScrollYLayout && isWorkspaceFixedTableScrollPath(pathname);
    const mainContainerClassName = useFixedTableScrollChain ? 'main-container' : 'main-container main-container--natural-scroll';

    const { tabs, onTabsChange } = useSyncedPageTabs();

    const [collapsed, setCollapsed] = useState(() => localStorage.getItem(LS_SELECTION_WORKSPACE_COLLAPSED) === 'true');
    const [searchOpen, setSearchOpen] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [projectDetail, setProjectDetail] = useState(null);
    const [projectResolved, setProjectResolved] = useState(false);
    const prevIsMobileRef = useRef(null);

    const openSearchModal = useCallback(() => setSearchOpen(true), []);
    const closeSearchModal = useCallback(() => setSearchOpen(false), []);

    const handleToggleSider = useCallback(() => {
        if (isMobile) {
            setMobileMenuOpen(true);
            return;
        }
        setCollapsed(prev => {
            const next = !prev;
            safeLsSet(LS_SELECTION_WORKSPACE_COLLAPSED, String(next));
            return next;
        });
    }, [isMobile]);

    const goParentList = useCallback(() => {
        navigate(SELECTION_WORKSPACE_PARENT_PATH);
    }, [navigate]);

    useEffect(() => {
        const handler = e => {
            if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                openSearchModal();
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [openSearchModal]);

    useEffect(() => {
        const prev = prevIsMobileRef.current;
        if (prev === null) {
            if (isMobile) setCollapsed(true);
        } else if (prev !== isMobile) {
            if (isMobile) {
                setCollapsed(true);
            } else {
                setCollapsed(localStorage.getItem(LS_SELECTION_WORKSPACE_COLLAPSED) === 'true');
                setMobileMenuOpen(false);
            }
        }
        prevIsMobileRef.current = isMobile;
    }, [isMobile]);

    const isCreateMode = pid === 'new';

    const menuEntries = useMemo(() => {
        if (!pid) return [];
        return buildSelectionWorkspaceMenuEntries(pid, { isCreateMode });
    }, [pid, isCreateMode]);

    const mobileMenuItems = menuEntries.map(row => ({
        key: row.key,
        label: row.label,
        disabled: row.disabled,
    }));
    const mobileSelectedKeys = useMemo(() => getSelectionMenuSelectedKeys(pathname, menuEntries), [menuEntries, pathname]);

    const handleMobileMenuClick = useCallback(
        ({ key }) => {
            navigate(key);
            setMobileMenuOpen(false);
        },
        [navigate],
    );

    useEffect(() => {
        if (!pid || isCreateMode) return;
        setProjectDetail(null);
        setProjectResolved(false);
        let cancelled = false;
        getProjestsApi({ id: pid })
            .then(res => {
                if (cancelled) return;
                setProjectResolved(true);
                if (res?.status_code !== 200 || !res?.data) return;
                const detail = res.data;
                const name = typeof detail.name === 'string' ? detail.name.trim() : '';
                if (name) {
                    rememberWorkspaceProjectTabTitle(pid, name, 'selection');
                }
                setProjectDetail(detail);
            })
            .catch(() => {
                if (!cancelled) {
                    setProjectResolved(true);
                }
            });
        return () => {
            cancelled = true;
        };
    }, [pid, isCreateMode]);

    if (!pid) {
        return (
            <div style={{ padding: 24 }}>
                <Result
                    status="404"
                    title="未找到该评议项目"
                    subTitle="路由缺少项目 ID"
                    extra={
                        <Button type="primary" onClick={goParentList}>
                            返回评议项目列表
                        </Button>
                    }
                />
            </div>
        );
    }

    if (!isCreateMode && !projectResolved) {
        return null;
    }

    if (!isCreateMode && projectDetail == null) {
        return (
            <div style={{ padding: 24 }}>
                <Result
                    status="404"
                    title="未找到该评议项目"
                    subTitle="请从评议项目列表进入或检查项目是否存在"
                    extra={
                        <Button type="primary" onClick={goParentList}>
                            返回评议项目列表
                        </Button>
                    }
                />
            </div>
        );
    }

    const cardChainStyle = useFixedTableScrollChain
        ? {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
          }
        : {
              flex: 'none',
              minHeight: '100%',
              alignSelf: 'stretch',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
          };

    const cardBodyChainStyle = useFixedTableScrollChain
        ? {
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
          }
        : {
              flex: 'none',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'visible',
              minHeight: 'auto',
          };

    return (
        <Layout style={{ minHeight: '100vh' }}>
            {!isMobile && <SelectionSider collapsed={collapsed} menuEntries={menuEntries} />}
            <Layout style={rightColumnStyle}>
                <div style={branchNoShrinkStyle}>
                    <AppHeader siderCollapsed={collapsed} onToggleSider={handleToggleSider} onOpenSearch={openSearchModal} />
                </div>
                {!isMobile && (
                    <div style={branchNoShrinkStyle}>
                        <PageTabs tabs={tabs} onTabsChange={onTabsChange} />
                    </div>
                )}
                <Content
                    component="main"
                    className={mainContainerClassName}
                    style={{
                        margin: token.margin,
                        padding: 0,
                        display: 'flex',
                        flex: 1,
                        flexDirection: 'column',
                        minWidth: 0,
                        minHeight: 0,
                        background: token.colorBgLayout,
                    }}
                >
                    <UserWatermark>
                        <Card
                            className="main-container-card"
                            data-table-scroll-layout={useFixedTableScrollChain ? 'fixed' : 'natural'}
                            style={cardChainStyle}
                            styles={{
                                body: cardBodyChainStyle,
                            }}
                        >
                            <KeepAliveOutlet aliveKeys={tabs.map(t => t.key)} />
                        </Card>
                    </UserWatermark>
                </Content>
            </Layout>
            <SearchModal open={searchOpen} onClose={closeSearchModal} />
            {isMobile && (
                <Drawer
                    placement="left"
                    open={mobileMenuOpen}
                    onClose={() => setMobileMenuOpen(false)}
                    size={240}
                    closable={false}
                    styles={{
                        header: { display: 'none' },
                        body: { padding: 0, display: 'flex', flexDirection: 'column' },
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            height: 56,
                            padding: '0 20px',
                            flexShrink: 0,
                            borderBottom: `1px solid ${token.colorSplit}`,
                        }}
                    >
                        <img src="/logo.svg" alt="logo" style={{ width: 26, height: 26 }} />
                        <span style={{ fontWeight: 700, fontSize: 18, color: token.colorTextHeading }}>华媒康讯•智管平台</span>
                    </div>
                    <div style={{ padding: '8px 12px', flexShrink: 0, borderBottom: `1px solid ${token.colorSplit}` }}>
                        <Button
                            type="text"
                            size="small"
                            icon={<i className="bi bi-arrow-left-circle-fill" aria-hidden style={{ color: token.colorTextTertiary, opacity: 0.75 }} />}
                            onClick={() => {
                                setMobileMenuOpen(false);
                                navigate(SELECTION_WORKSPACE_PARENT_PATH);
                            }}
                            style={{ color: token.colorText, width: '100%', textAlign: 'left' }}
                            aria-label="返回评议项目列表"
                        >
                            返回评议项目列表
                        </Button>
                    </div>
                    <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden' }}>
                        <Menu mode="inline" items={mobileMenuItems} selectedKeys={mobileSelectedKeys} onClick={handleMobileMenuClick} style={{ borderRight: 'none', fontSize: 16 }} />
                    </div>
                </Drawer>
            )}
        </Layout>
    );
}
